/** @module The three tips a new person gets before their first scan, and where photos go. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Hand, Lightbulb, MonitorSmartphone, ShieldCheck } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { Sheet } from "@/components/ui/overlays";

const SEEN_KEY = "scanIntroSeen";

async function fetchSettings() {
  const { data, response } = await api.GET("/api/v1/me/settings", {});
  if (!data) throw problemFrom(response);
  return data;
}

/**
 * Whether the tips are due, and a way to mark them read.
 *
 * Stored with the account, not the device: a tester who switches phones has read them.
 */
export function useScanIntro(): { due: boolean; markSeen: () => void } {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["me", "settings"], queryFn: fetchSettings });
  const save = useMutation({
    mutationFn: async () => {
      const current = settings.data;
      if (!current) return null;
      // The server replaces the whole ui object; everything else must travel along.
      const { data, response } = await api.PATCH("/api/v1/me/settings", {
        body: {
          condition_factors: current.condition_factors,
          ui: { ...current.ui, [SEEN_KEY]: true },
        },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(["me", "settings"], data);
    },
  });
  // Closed counts at once; the server's answer only makes it last.
  const [closed, setClosed] = useState(false);
  const due = settings.isSuccess && settings.data.ui[SEEN_KEY] !== true && !closed;
  return {
    due,
    markSeen: () => {
      if (!due) return;
      setClosed(true);
      save.mutate();
    },
  };
}

function Tip({ icon, title, text }: { icon: ReactNode; title: string; text: string }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 shrink-0 text-(--accent) [&>svg]:size-5">{icon}</span>
      <span className="flex flex-col gap-0.5">
        <b>{title}</b>
        <span className="t-sm t-muted">{text}</span>
      </span>
    </li>
  );
}

export function FirstRunSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Sheet open={open} onClose={onClose} label={t("scan.intro.title")}>
      <div className="flex flex-col gap-5">
        <h2 className="t-h3">{t("scan.intro.title")}</h2>
        <ul className="flex flex-col gap-4">
          <Tip icon={<Hand />} title={t("scan.intro.handTitle")} text={t("scan.intro.hand")} />
          <Tip
            icon={<Lightbulb />}
            title={t("scan.intro.lightTitle")}
            text={t("scan.intro.light")}
          />
          <Tip
            icon={<MonitorSmartphone />}
            title={t("scan.intro.holderTitle")}
            text={t("scan.intro.holder")}
          />
        </ul>
        <div className="callout callout-accent">
          <ShieldCheck />
          <span>{t("scan.intro.photos")}</span>
        </div>
        <button type="button" className="btn btn-primary btn-block" onClick={onClose}>
          {t("scan.intro.start")}
        </button>
      </div>
    </Sheet>
  );
}
